import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Phone, BookOpen, DollarSign, Pill, UtensilsCrossed } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackForecastCtaClicked } from '../utils/analytics';

// Blog post metadata - read times calculated at 200 words/min, minimum 2 min
export const blogPosts = [
  // ===== ORIGINAL 15 FOUNDATION POSTS =====
  {
    slug: 'what-is-compounded-semaglutide',
    title: 'What Is Compounded Semaglutide — And How Is It Different From Ozempic?',
    category: 'Foundation',
    description: 'Compounded semaglutide and Ozempic both contain the same active ingredient — but they\'re not the same thing. Here\'s what actually makes them different.',
    readTime: '4 min read'
  },
  {
    slug: 'how-glp1-medications-work',
    title: 'How GLP-1 Medications Actually Work in Your Body (Plain English)',
    category: 'Foundation',
    description: 'GLP-1 medications are everywhere — but most explanations are either too technical or too vague. Here\'s how they actually work, in plain language.',
    readTime: '5 min read'
  },
  {
    slug: 'what-is-tirzepatide',
    title: 'What Is Tirzepatide and Why Are So Many Doctors Recommending It?',
    category: 'Foundation',
    description: 'Tirzepatide is newer than semaglutide and clinical data shows stronger average results. Here\'s what it is, how it works, and who it tends to work best for.',
    readTime: '4 min read'
  },
  {
    slug: 'compounded-vs-brand-name-glp1',
    title: 'Compounded vs Brand Name GLP-1 — What\'s the Actual Difference?',
    category: 'Foundation',
    description: 'Both contain the same active ingredient. The differences are in cost, access, and how they\'re made. Here\'s what actually matters when comparing them.',
    readTime: '4 min read'
  },
  {
    slug: 'what-to-eat-on-semaglutide-meal-guide',
    title: 'What to Eat on Semaglutide — A Simple Weekly Meal Guide',
    category: 'Diet & Food',
    description: 'What you eat on semaglutide significantly affects both your results and your side effect experience. Here\'s a practical weekly guide.',
    readTime: '5 min read'
  },
  {
    slug: 'glp1-grocery-list',
    title: 'The GLP-1 Grocery List — What to Buy, What to Skip',
    category: 'Diet & Food',
    description: 'A practical, no-fluff grocery list for people on GLP-1 medications. Built around protein, easy digestion, and foods that support your results.',
    readTime: '4 min read'
  },
  {
    slug: 'foods-that-worsen-glp1-nausea',
    title: 'Foods That Make Nausea Worse on GLP-1s (And What to Eat Instead)',
    category: 'Diet & Food',
    description: 'Nausea is one of the most common early side effects of GLP-1 medications — and certain foods make it significantly worse.',
    readTime: '4 min read'
  },
  {
    slug: 'protein-on-glp1-how-much',
    title: 'How Much Protein Do You Actually Need on a GLP-1 Program?',
    category: 'Diet & Food',
    description: 'Protein is the most important nutritional variable on a GLP-1 program. Here\'s how much you actually need and why it matters.',
    readTime: '4 min read'
  },
  {
    slug: 'glp1-side-effects-how-to-fix',
    title: 'The Most Common GLP-1 Side Effects — And How to Actually Fix Them',
    category: 'Side Effects',
    description: 'Most GLP-1 side effects are manageable with the right approach. Here\'s what\'s actually happening and specific strategies that help.',
    readTime: '5 min read'
  },
  {
    slug: 'semaglutide-nausea-how-to-reduce',
    title: 'Nausea on Semaglutide — Why It Happens and How to Reduce It Fast',
    category: 'Side Effects',
    description: 'Nausea is the most common semaglutide side effect — but it\'s manageable. Here\'s exactly why it happens and the fastest ways to reduce it.',
    readTime: '4 min read'
  },
  {
    slug: 'fatigue-on-glp1-medications',
    title: 'Fatigue on GLP-1s — Is It Normal and What Helps?',
    category: 'Side Effects',
    description: 'Feeling tired on a GLP-1? It\'s more common than most people expect. Here\'s why it happens and what actually helps.',
    readTime: '3 min read'
  },
  {
    slug: 'hair-loss-glp1-medications',
    title: 'Hair Loss on GLP-1 Medications — What\'s Causing It and What to Do',
    category: 'Side Effects',
    description: 'Some people experience hair thinning on GLP-1 medications. Here\'s what\'s actually causing it, whether it\'s permanent, and what you can do.',
    readTime: '4 min read'
  },
  {
    slug: 'exercise-on-glp1-medications',
    title: 'The Best Exercise Approach While on GLP-1 Medications',
    category: 'Lifestyle',
    description: 'Exercise on GLP-1 medications requires a different approach than typical weight loss programs. Here\'s what actually works — and what to avoid.',
    readTime: '4 min read'
  },
  {
    slug: 'sleep-stress-glp1-weight-loss',
    title: 'Sleep, Stress, and Weight Loss — Why Your Habits Matter as Much as the Medication',
    category: 'Lifestyle',
    description: 'GLP-1 medications work best when lifestyle supports them. Sleep and stress have a bigger impact on your results than most people realize.',
    readTime: '5 min read'
  },
  {
    slug: 'first-30-days-glp1-program',
    title: 'What to Expect in Your First 30 Days on a GLP-1 Program',
    category: 'Getting Started',
    description: 'The first 30 days on a GLP-1 program are different from what most people expect. Here\'s an honest, week-by-week picture.',
    readTime: '5 min read'
  },
  // ===== 15 NEW COMPANION POSTS =====
  {
    slug: 'tirzepatide-month-by-month-expectations',
    title: 'What to Expect Month by Month on Tirzepatide',
    category: 'Results & Timelines',
    description: 'Tirzepatide results don\'t happen all at once — they build over time in a predictable pattern. Here\'s an honest month-by-month breakdown of what most people experience.',
    readTime: '5 min read'
  },
  {
    slug: 'why-glp1-stops-being-effective',
    title: 'Why GLP-1 Medications Lose Effectiveness — And What to Do About It',
    category: 'Troubleshooting',
    description: 'If your GLP-1 medication seems to be working less well than it used to, you\'re not imagining it. Here\'s what\'s actually happening and what the options are.',
    readTime: '5 min read'
  },
  {
    slug: 'maintain-weight-loss-after-glp1',
    title: 'How to Maintain Weight Loss After Stopping GLP-1 Medications',
    category: 'Results & Timelines',
    description: 'Weight regain after stopping GLP-1 medications is common — but not inevitable. Here\'s what the evidence shows and what actually works for maintaining results.',
    readTime: '5 min read'
  },
  {
    slug: 'choosing-semaglutide-vs-tirzepatide',
    title: 'How to Choose Between Semaglutide and Tirzepatide for Your Specific Situation',
    category: 'Comparisons',
    description: 'Semaglutide and tirzepatide both work — but they\'re not identical. Here\'s how to think about which one makes more sense for your specific situation.',
    readTime: '5 min read'
  },
  {
    slug: 'what-is-nad-plus-glp1-programs',
    title: 'What Is NAD+ and Why Are People Adding It to GLP-1 Programs?',
    category: 'NAD+',
    description: 'NAD+ has become one of the most talked-about additions to GLP-1 programs. Here\'s what it actually is, what the evidence shows, and why people are stacking it with semaglutide and tirzepatide.',
    readTime: '5 min read'
  },
  {
    slug: 'glp1-program-without-insurance',
    title: 'How to Get Started on a GLP-1 Program Without Insurance',
    category: 'Getting Started',
    description: 'Insurance coverage for GLP-1 weight loss medications is inconsistent at best. Here\'s a practical guide to accessing a legitimate program without relying on insurance.',
    readTime: '5 min read'
  },
  {
    slug: 'questions-to-ask-before-starting-semaglutide',
    title: 'What Questions to Ask Before Starting Semaglutide',
    category: 'Getting Started',
    description: 'Starting semaglutide is a real medical decision. These are the questions worth asking — and getting answered — before you begin.',
    readTime: '5 min read'
  },
  {
    slug: 'prepare-first-week-tirzepatide',
    title: 'How to Prepare for Your First Week on Tirzepatide',
    category: 'Getting Started',
    description: 'The first week on tirzepatide sets the tone for the entire program. Here\'s exactly how to prepare so the adjustment is as smooth as possible.',
    readTime: '5 min read'
  },
  {
    slug: 'why-glp1-results-vary-between-people',
    title: 'Why Some People Lose Twice as Much as Others on the Same GLP-1 Medication',
    category: 'Results & Timelines',
    description: 'Two people on the same GLP-1 medication at the same dose can have dramatically different results. Here\'s what actually explains the difference.',
    readTime: '5 min read'
  },
  {
    slug: 'what-happens-when-you-stop-glp1',
    title: 'What Happens to Your Body When You Stop Taking GLP-1 Medications',
    category: 'Results & Timelines',
    description: 'Stopping GLP-1 medications produces a predictable set of changes in your body. Here\'s an honest look at what to expect and how to manage the transition.',
    readTime: '5 min read'
  },
  {
    slug: 'break-weight-loss-plateau-glp1',
    title: 'How to Break a Weight Loss Plateau on GLP-1 Medications',
    category: 'Troubleshooting',
    description: 'Weight loss plateaus on GLP-1 medications are common and frustrating. Here\'s what\'s actually causing them and specific strategies that work.',
    readTime: '5 min read'
  },
  {
    slug: 'nad-plus-energy-glp1-program',
    title: 'NAD+ for Energy on a GLP-1 Program — Does It Actually Work?',
    category: 'NAD+',
    description: 'Fatigue is one of the most common complaints on GLP-1 programs. NAD+ is increasingly being used to address it. Here\'s what the evidence shows.',
    readTime: '5 min read'
  },
  {
    slug: 'medical-weight-loss-vs-diet-programs',
    title: 'The Real Difference Between Medical Weight Loss and Diet Programs',
    category: 'Education',
    description: 'Medical weight loss and traditional diet programs work through fundamentally different mechanisms. Here\'s what actually separates them and why it matters.',
    readTime: '5 min read'
  },
  {
    slug: 'how-to-get-compounded-glp1-safely',
    title: 'How to Get Compounded GLP-1 Medications Legally and Safely',
    category: 'Getting Started',
    description: 'Compounded GLP-1 medications are legal and accessible — but the quality varies significantly depending on the source. Here\'s how to get them safely.',
    readTime: '5 min read'
  },
  {
    slug: 'why-glp1-works-when-other-things-didnt',
    title: 'Why GLP-1 Medications Work When Everything Else Didn\'t',
    category: 'Education',
    description: 'If you\'ve tried other weight loss approaches without lasting results, there\'s a specific biological reason. Here\'s why GLP-1 medications work differently.',
    readTime: '5 min read'
  },
  // ===== 9 SERMORELIN POSTS =====
  {
    slug: 'what-is-sermorelin',
    title: 'What Is Sermorelin and How Does It Work?',
    category: 'Sermorelin',
    description: 'Sermorelin is one of the most talked-about peptides in medical wellness. Here\'s a clear, plain-language explanation of what it actually is and how it works in your body.',
    readTime: '5 min read'
  },
  {
    slug: 'sermorelin-vs-hgh',
    title: 'Sermorelin vs HGH — What\'s the Actual Difference?',
    category: 'Sermorelin',
    description: 'Sermorelin and HGH are often mentioned together — but they work very differently. Here\'s a clear breakdown of what separates them and why it matters.',
    readTime: '5 min read'
  },
  {
    slug: 'sermorelin-candidate',
    title: 'Who Is a Good Candidate for Sermorelin?',
    category: 'Sermorelin',
    description: 'Sermorelin isn\'t for everyone — but for the right person, it addresses things that diet and exercise alone often can\'t. Here\'s how to think about whether it makes sense for you.',
    readTime: '5 min read'
  },
  {
    slug: 'sermorelin-sleep-recovery',
    title: 'What Does Sermorelin Actually Do for Sleep and Recovery?',
    category: 'Sermorelin',
    description: 'Sleep and recovery are two of the most consistent benefits people report on sermorelin. Here\'s the science behind why and what to realistically expect.',
    readTime: '5 min read'
  },
  {
    slug: 'sermorelin-muscle-tone',
    title: 'Sermorelin for Muscle Tone — What the Evidence Shows',
    category: 'Sermorelin',
    description: 'Sermorelin is increasingly used to support muscle tone and body composition. Here\'s what the evidence actually shows and what realistic expectations look like.',
    readTime: '5 min read'
  },
  {
    slug: 'how-long-sermorelin-takes-to-work',
    title: 'How Long Does Sermorelin Take to Work?',
    category: 'Sermorelin',
    description: 'Sermorelin results don\'t happen overnight. Here\'s an honest timeline of what to expect and when — so you know what\'s working and what\'s worth waiting for.',
    readTime: '5 min read'
  },
  {
    slug: 'sermorelin-glp1-combination',
    title: 'Why People Are Combining Sermorelin With GLP-1 Programs',
    category: 'Sermorelin',
    description: 'The combination of sermorelin and GLP-1 medications is becoming increasingly common in medically supervised programs. Here\'s why the two work well together.',
    readTime: '5 min read'
  },
  {
    slug: 'full-optimization-stack-glp1-nad-sermorelin',
    title: 'The Full Optimization Stack — GLP-1 + NAD+ + Sermorelin',
    category: 'Sermorelin',
    description: 'More people are combining GLP-1 medications, NAD+, and sermorelin into a coordinated wellness program. Here\'s what each does and why the combination makes sense.',
    readTime: '6 min read'
  },
  {
    slug: 'complete-medical-wellness-program',
    title: 'What a Complete Medical Wellness Program Actually Looks Like',
    category: 'Sermorelin',
    description: 'Most programs address one thing. A complete medical wellness program addresses everything at once. Here\'s what that actually looks like in practice.',
    readTime: '6 min read'
  },
  // ===== 5 VIRAL CROSSOVER POSTS =====
  {
    slug: 'the-plateau-nobody-warns-you-about',
    title: 'The Plateau Nobody Warns You About — And the Peptide Conversation That Changes It',
    category: 'Troubleshooting',
    description: 'Sarah did everything right for four months. Then weight loss stopped completely. Here\'s what was actually going on and why the solution wasn\'t more restriction.',
    readTime: '7 min read'
  },
  {
    slug: 'perimenopause-three-years-nobody-told-me',
    title: 'I Thought I Was Just "Getting Older." I Was Actually in Perimenopause for Three Years Before Anyone Told Me.',
    category: "Women's Health",
    description: 'For three years, every doctor told her "your labs are normal." Here\'s what was actually happening and why so many women spend years not knowing.',
    readTime: '8 min read'
  },
  {
    slug: 'nobody-talks-about-muscle-loss-glp1',
    title: 'Everyone Talks About Losing Fat. Almost Nobody Talks About What Happens to Your Muscle While You Do It.',
    category: 'Education',
    description: 'You can lose 40 pounds and end up with a body that functions worse than before. Here\'s what determines whether you end up leaner and stronger, or just smaller.',
    readTime: '7 min read'
  },
  {
    slug: 'youre-just-tired-because-dieting-half-right',
    title: '"You\'re Just Tired Because You\'re Dieting." That\'s What They Told Her. They Were Half Right.',
    category: 'NAD+',
    description: 'Amanda heard the same thing from three different people about her exhaustion. They weren\'t wrong exactly — they just weren\'t giving her the whole picture.',
    readTime: '7 min read'
  },
  {
    slug: 'plateau-might-be-hormonal-not-discipline',
    title: 'The Weight Loss Advice Industry Doesn\'t Want You to Know This: Your Plateau Might Be Hormonal, Not a Discipline Problem',
    category: 'Troubleshooting',
    description: 'There\'s a multi-billion dollar industry built on one assumption: if your weight loss stalled, you must be doing something wrong. For many women over 35, this is simply wrong.',
    readTime: '8 min read'
  },
  // ===== 12 PEPTIDE POSTS =====
  {
    slug: 'injury-wouldnt-heal-bpc-157',
    title: `The Injury That Wouldn't Heal — Until Someone Mentioned BPC-157`,
    category: 'Recovery & Healing',
    description: `Tyler did everything a physical therapist tells you to do. Eighteen months later, his shoulder still wasn't fully healed. Here's what that plateau is usually about.`,
    readTime: '7 min read'
  },
  {
    slug: 'what-is-wolverine-stack',
    title: `What Is the Wolverine Stack — And Why Is Everyone Suddenly Talking About It?`,
    category: 'Recovery & Healing',
    description: `The nickname for a combination of two research peptides studied for complementary aspects of tissue repair. Here's what it actually is.`,
    readTime: '7 min read'
  },
  {
    slug: 'bpc-157-vs-tb-500-differences',
    title: `BPC-157 vs TB-500 — What Each One Actually Does Differently`,
    category: 'Recovery & Healing',
    description: `They're mentioned together so often it's easy to assume they do the same thing. They don't. Here's how each is studied differently.`,
    readTime: '6 min read'
  },
  {
    slug: 'why-body-feels-older-mitochondrial-piece',
    title: `Why Your Body Feels "Older" Than It Should — The Mitochondrial Piece Nobody Explains`,
    category: 'Energy & Cellular Health',
    description: `There's a specific kind of tired that doesn't respond to sleep. The explanation usually isn't your habits — it's at the cellular level.`,
    readTime: '7 min read'
  },
  {
    slug: 'what-is-mots-c-exercise-in-a-vial',
    title: `What Is MOTS-c — The Peptide Behind the "Exercise in a Vial" Conversation`,
    category: 'Energy & Cellular Health',
    description: `If you've researched peptides for energy, you've come across "exercise in a vial" attached to MOTS-c. Here's what it actually is.`,
    readTime: '6 min read'
  },
  {
    slug: 'glutathione-antioxidant-after-30',
    title: `Glutathione — The Antioxidant Your Body Stops Making Enough Of After 30`,
    category: 'Energy & Cellular Health',
    description: `Arguably more important than collagen or vitamin C — and your body makes meaningfully less of it starting in your 30s. Here's why it matters.`,
    readTime: '6 min read'
  },
  {
    slug: 'hadnt-slept-through-night-two-years',
    title: `I Hadn't Slept Through the Night in Two Years — Here's What Finally Worked`,
    category: 'Sleep',
    description: `Rachel had tried everything on every "sleep better" list. She was still waking up multiple times a night for over two years. Here's what the issue actually was.`,
    readTime: '7 min read'
  },
  {
    slug: 'what-is-dsip-sleep-peptide',
    title: `What Is DSIP and Does It Actually Help You Sleep?`,
    category: 'Sleep',
    description: `A clear, no-hype explanation of what DSIP is, what the research has examined regarding sleep quality, and realistic expectations.`,
    readTime: '6 min read'
  },
  {
    slug: 'pt-141-explained-mens-conversation',
    title: `The Conversation Men Don't Have Until They're Already Frustrated — PT-141 Explained`,
    category: "Men's Health",
    description: `PT-141 works through a brain pathway rather than blood flow. Here's what it is, who explores it, and how it compares to DirectMax.`,
    readTime: '7 min read'
  },
  {
    slug: 'low-libido-brain-chemistry-not-blood-flow',
    title: `Why Low Libido Isn't Always About Blood Flow — The Brain Chemistry Most Men Don't Know About`,
    category: "Men's Health",
    description: `Almost every conversation about men's sexual health defaults to blood flow. For a lot of men, that's only part of the actual picture.`,
    readTime: '7 min read'
  },
  {
    slug: 'lipo-b-whats-actually-in-it',
    title: `Lipo-B — The Compound Most People Add Without Realizing What's In It`,
    category: 'Energy & Cellular Health',
    description: `Lipo-B comes up constantly in weight loss programs, but most people couldn't tell you what's in the vial. Here's a straightforward breakdown.`,
    readTime: '6 min read'
  },
  {
    slug: 'why-people-stacking-multiple-things',
    title: `Why More People Are Stacking 3-4 Things Instead of Just One`,
    category: 'Education',
    description: `A few years ago, the typical conversation involved one product. Now it increasingly involves a combination — and there's a specific reason why.`,
    readTime: '7 min read'
  }
];

// ===== POST PUBLISH DATES =====
// Posts were written in batches; this maps each batch to its publish month.
// Companion batch (15 posts, written April 2026)
const COMPANION_SLUGS = new Set([
  'tirzepatide-month-by-month-expectations',
  'why-glp1-stops-being-effective',
  'maintain-weight-loss-after-glp1',
  'choosing-semaglutide-vs-tirzepatide',
  'what-is-nad-plus-glp1-programs',
  'glp1-program-without-insurance',
  'questions-to-ask-before-starting-semaglutide',
  'prepare-first-week-tirzepatide',
  'why-glp1-results-vary-between-people',
  'what-happens-when-you-stop-glp1',
  'break-weight-loss-plateau-glp1',
  'nad-plus-energy-glp1-program',
  'medical-weight-loss-vs-diet-programs',
  'how-to-get-compounded-glp1-safely',
  'why-glp1-works-when-other-things-didnt'
]);
// Viral Crossover batch (5 posts, written June 2026)
const VIRAL_SLUGS = new Set([
  'the-plateau-nobody-warns-you-about',
  'perimenopause-three-years-nobody-told-me',
  'nobody-talks-about-muscle-loss-glp1',
  'youre-just-tired-because-dieting-half-right',
  'plateau-might-be-hormonal-not-discipline'
]);
// Peptide batch (12 posts, written June 2026)
const PEPTIDE_SLUGS = new Set([
  'injury-wouldnt-heal-bpc-157',
  'what-is-wolverine-stack',
  'bpc-157-vs-tb-500-differences',
  'why-body-feels-older-mitochondrial-piece',
  'what-is-mots-c-exercise-in-a-vial',
  'glutathione-antioxidant-after-30',
  'hadnt-slept-through-night-two-years',
  'what-is-dsip-sleep-peptide',
  'pt-141-explained-mens-conversation',
  'low-libido-brain-chemistry-not-blood-flow',
  'lipo-b-whats-actually-in-it',
  'why-people-stacking-multiple-things'
]);

// Returns the month/year a post was written
export const getPostDate = (slug, category) => {
  if (VIRAL_SLUGS.has(slug)) return 'June 2026';
  if (PEPTIDE_SLUGS.has(slug)) return 'June 2026';
  if (category === 'Sermorelin') return 'April 2026';
  if (COMPANION_SLUGS.has(slug)) return 'April 2026';
  return 'March 2026';
};

// Category colors
const categoryColors = {
  'Foundation': 'bg-[#3B82F6]/20 text-[#3B82F6]',
  'Diet & Food': 'bg-[#22C55E]/20 text-[#22C55E]',
  'Side Effects': 'bg-[#F59E0B]/20 text-[#F59E0B]',
  'Lifestyle': 'bg-[#8B5CF6]/20 text-[#8B5CF6]',
  'Getting Started': 'bg-[#EC4899]/20 text-[#EC4899]',
  'Results & Timelines': 'bg-[#06B6D4]/20 text-[#06B6D4]',
  'Troubleshooting': 'bg-[#EF4444]/20 text-[#EF4444]',
  'Comparisons': 'bg-[#14B8A6]/20 text-[#14B8A6]',
  'NAD+': 'bg-[#A855F7]/20 text-[#A855F7]',
  'Education': 'bg-[#6366F1]/20 text-[#6366F1]',
  'Sermorelin': 'bg-[#F97316]/20 text-[#F97316]',
  "Women's Health": 'bg-[#DB2777]/20 text-[#DB2777]',
  'Recovery & Healing': 'bg-[#10B981]/20 text-[#10B981]',
  'Energy & Cellular Health': 'bg-[#EAB308]/20 text-[#EAB308]',
  'Sleep': 'bg-[#818CF8]/20 text-[#818CF8]',
  "Men's Health": 'bg-[#0EA5E9]/20 text-[#0EA5E9]'
};

export const Blog = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');

  // Get unique categories
  const categories = ['All', ...new Set(blogPosts.map(p => p.category))];

  // Filter posts based on active category
  const filteredPosts = activeCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#0B1020]">
      <SEOHead 
        title="GLP-1 Education Blog"
        description="Real answers about GLP-1 medications. No fluff, no sales pitch — just clear, practical information about semaglutide, tirzepatide, and medical weight loss."
        canonicalPath="/blog"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1020]/95 backdrop-blur-sm border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-xl font-bold text-[#F8FAFC]">Bryan Maldonado</Link>
            <div className="flex items-center gap-4">
              <a href="tel:801-651-8363" className="hidden sm:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <Phone size={18} />
                <span>801-651-8363</span>
              </a>
              <Button onClick={() => {
                trackForecastCtaClicked('blog_header');
                navigate('/calculator');
              }} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                90-Day Forecast
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-full mb-6">
            <BookOpen size={18} className="text-[#3B82F6]" />
            <span className="text-[#3B82F6] text-sm font-semibold">GLP-1 Education Center</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] mb-4">
            Real Answers About GLP-1 Medications
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
            No fluff, no sales pitch — just clear, practical information to help you make informed decisions about your weight loss journey.
          </p>
        </div>
      </section>

      {/* Forecast CTA */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-[#3B82F6]/10 to-[#22C55E]/10 border-[#3B82F6]/30 p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-[#F8FAFC] mb-1">Curious what your results could look like?</h3>
                <p className="text-[#94A3B8] text-sm">Get a personalized 90-day estimate — no consultation required to see results.</p>
              </div>
              <div className="flex flex-col items-center sm:items-end">
                <Link 
                  to="/calculator"
                  onClick={() => trackForecastCtaClicked('blog_cta_card')}
                  className="inline-flex items-center justify-center bg-[#22C55E] hover:bg-[#16A34A] text-white font-medium px-6 py-3 rounded-md transition-colors whitespace-nowrap"
                >
                  Start My 90-Day Forecast
                  <ArrowRight className="ml-2" size={18} />
                </Link>
                <p className="text-[#64748B] text-xs mt-2">Takes about 2–3 minutes — no call required</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Free Tools Banner */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#1E293B] border-[#334155] p-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-1">Free GLP-1 Tools</h3>
              <p className="text-[#94A3B8] text-sm">Make informed decisions with our free calculators</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Link 
                to="/cost-calculator"
                className="inline-flex items-center gap-2 bg-[#22C55E]/10 border border-[#22C55E]/30 text-[#22C55E] font-medium px-4 py-2 rounded-lg hover:bg-[#22C55E]/20 transition-colors text-sm"
              >
                <DollarSign size={16} />
                Cost Calculator
              </Link>
              <Link 
                to="/medication-match"
                className="inline-flex items-center gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6] font-medium px-4 py-2 rounded-lg hover:bg-[#3B82F6]/20 transition-colors text-sm"
              >
                <Pill size={16} />
                Medication Match
              </Link>
              <Link 
                to="/meal-plan"
                className="inline-flex items-center gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] font-medium px-4 py-2 rounded-lg hover:bg-[#F59E0B]/20 transition-colors text-sm"
              >
                <UtensilsCrossed size={16} />
                Meal Planner
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-6xl mx-auto">
          <div 
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible" 
            data-testid="category-filters"
            style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                  activeCategory === category
                    ? 'bg-[#3B82F6] text-white'
                    : 'bg-[#1E293B] text-[#94A3B8] hover:bg-[#2D3B4F] hover:text-[#F8FAFC]'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group"
                data-testid={`blog-card-${post.slug}`}
              >
                <Card className="bg-[#121A2B] border-[#1E293B] p-6 h-full hover:border-[#3B82F6] transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2 py-1 rounded text-xs ${categoryColors[post.category]}`}>
                      {post.category}
                    </span>
                    <span className="text-[#64748B] text-xs">{post.readTime}</span>
                    <span className="text-[#64748B] text-xs">•</span>
                    <span className="text-[#64748B] text-xs">{getPostDate(post.slug, post.category)}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#F8FAFC] mb-2 group-hover:text-[#3B82F6] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-[#94A3B8] text-sm line-clamp-2">
                    {post.description}
                  </p>
                  <div className="mt-4 flex items-center text-[#3B82F6] text-sm font-medium">
                    Read Article
                    <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#94A3B8] mb-4">© 2024 Bryan Maldonado. All rights reserved.</p>
          <p className="text-[#64748B] text-sm max-w-2xl mx-auto">
            All the information on this site is educational. Nothing here constitutes medical advice. Bryan Maldonado is a licensed professional and all programs include medical oversight.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
